import { DefaultMessages, ProgramThemeMessages, SegmentSpecificMessages } from '@sheerid/jslib';
import { RecursivePartial } from '../types/types';


type LibraryMessages = DefaultMessages & SegmentSpecificMessages & ProgramThemeMessages;

/**
 * Feel free to modify this type for this project (only) here
 * For example, if you add a whole new block of text that the jslib does not have a spot for already
 *
 * This is not necessary if you are just overriding text for a pre-existing message id
 */
type ThisProjectMessages = {
    softwareTypes: {
        online: string,
        desktop: string,
        tax: string,
    },
    errorId: {
        invalidCourseType: string,
        invalidQuickBooksDesktopVersion: string,
        invalidSoftwareType: string,
        invalidConfirmEmail: string,
        nonMatchEmail: string,
    },
    step: {
        personalInfo: {
            schoolNotFound: string,
            whySchoolEmail: string,
            subtitleUS: string,
        },
        success: {
            createAccount: string,
            intuitTeamUS: string,
            bestWishesUS: string,
            titleUS: string,
            online: {
                title: string,
                subtitle: string,
                subtitleCA: string,
                accessLink: string,
                bestWishes: string,
                bullet1: string,
                bullet2: string,
                nextSteps: string,
                nextStepsCA: string,
                createAccount: string,
                onlineLogin: string,
                getStarted: string,
                checkOut: string,
            },
            desktop: {
                title: string,
                subtitle: string,
                accessLink: string,
                bulletOne: string,
                bulletTwo: string,
                bulletThree: string,
                bestWishes: string,
                contactIntuit: string,
                subtitleCA: string,
            },
            tax: {
                title: string,
                titleUS: string,
                subtitle: string,
                subtitleCA: string,
                accessLink: string,
                bulletOne: string,
                bulletTwo: string,
                bulletThree: string,
                bestWishes: string,
                bestWishesUS: string,
                downloadLinkInstructionsCA: string,
            },
        },
        docUpload: {
            footer: string,
        },
    },
    birthDatePlaceholder: string,
    confirmEmailAddress: string,
    other: string,
    'accounting/bookkeeping': string,
    entrepreneurship: string,
    business: string,
    tax: string,
    quickbooks: string,
    courseType: string,
    quickbooksDesktopVersion: string,
    returnToIntuit: string,
    intuitEducationTeamUS: string,
    intuitEducationTeam: string,
    contactIntuit: string,
    infoShared: string,
    rulesOptInLabel: string,
    termsOptInLabel: string,
};

/**
 * Define the actual language strings here, as needed.
 * This object should be sparse, so you inherit language from the library. Only override what you need to.
 *
 * The complicated type ensures your object adheres to the library's type(s), as well as any additional type you've
 * created in ThisProjectMessages.
 */
export const messagesEN: RecursivePartial<LibraryMessages & ThisProjectMessages> = {
    firstName: 'First Name',
    lastName: 'Last Name',
    birthDate: 'Birth Date',
    birthDatePlaceholder: 'yyyy/mm/dd',
    emailAddress: 'School-Issued Email Address',
    confirmEmailAddress: 'Confirm School-Issued Email Address',
    schoolName: 'Institution Name',
    verifyMyTeacherStatus: 'Verify and continue',
    other: 'Other',
    'accounting/bookkeeping': 'Accounting/Bookkeeping',
    entrepreneurship: 'Entrepreneurship',
    business: 'Business',
    tax: 'Tax',
    quickbooks: 'QuickBooks',
    courseType: 'Type of course',
    quickbooksDesktopVersion: 'QuickBooks Desktop Version',
    returnToIntuit: 'Return to Intuit',
    intuitEducationTeam: '- The Intuit Team',
    intuitEducationTeamUS: 'Thanks!<br>The Intuit Team',
    rulesOptInLabel: 'By checking this box, you acknowledge that you’ve read and agree to these <a href="https://www.intuit.com/oicms/sandbox/quickbooks-student-success/#rules" target="_parent" class="sid-link"><b>Official Rules</b></a>',
    termsOptInLabel: 'By checking this box, you chose to participate in Intuit QuickBooks Education Sweepstakes you agree to our <a href="http://http-download.intuit.com/http.intuit/Downloads/Mac/Setup/WindowsConversion/terms.html" target="_blank" class="sid-link"><b>Terms</b></a> and have read and acknowledge our <a href="https://www.intuit.com/privacy/" target="_blank" class="sid-link"><b>Global Privacy Statement.</b></a>',
    contactIntuit: 'If that link doesn\'t work, <a class="sid-link" href="mailto:education@intuit.com">Education Support</a> can help.',
    softwareTypes: {
        online: 'QuickBooks Online',
        desktop: 'QuickBooks Desktop',
        tax: 'ProFile',
    },
    error: 'An error occurred',
    errorId: {
        invalidCourseType: 'Invalid course type',
        invalidQuickBooksDesktopVersion: 'Invalid QuickBooks Desktop version',
        invalidSoftwareType: 'Please return to Intuit.',
        invalidConfirmEmail: 'Invalid confirm email',
        nonMatchEmail: 'Emails do not match',
        invalidOptIn: 'Must be selected to continue',
    },
    step: {
        personalInfo: {
            title: 'Educator Registration',
            howDoesVerifyingWorkDetails: 'To be eligible for this offer, you must currently be employed as a teacher, faculty or staff at a K-12 school or university.<br><br>Intuit Inc. uses SheerID, a trusted partner, to verify that you are a current teacher, faculty or staff member. SheerID only collects information that assists in verifying your eligibility. Additional information or documentation may be requested.',
            subtitle: 'Educators and staff at accredited academic institutions are eligible. Complete the form below to confirm your eligibility and unlock your free license for {software}.',
            subtitleUS: 'Educators and staff at accredited academic institutions in the United States are eligible. Complete the form below to confirm your eligibility and unlock your free license for ProConnect.',
            schoolNotFound: 'Email us at <a class="sid-link" href="mailto:education@intuit.com">education@intuit.com</a> to determine if your school is eligible.',
            whySchoolEmail: 'Providing your school email address allows us to determine your eligibility for this exclusive teacher offer as quickly as possible.',
        },
        docUpload: {
            title: 'Verification documents',
            subtitle: 'We apologize, we couldn’t verify your educator status.<br />Documents will be reviewed in {estimatedWaitTime} by staff at SheerID, a trusted partner.',
            submitButtonLabel: 'Send documents',
            uploadInstructions: 'Upload your school-issued document that shows:',
            footer: 'Make sure the document image is clear, readable, and displays your full legal name as enrolled at your school. <strong>Do not include any confidential personal info such as SSN or banking details.</strong> Black these out by hand before you scan the form or use an editing feature on your computer.',
            howDoesVerifyingWorkDetails: `Document review is handled by SheerID, a trusted partner who specializes in verifying teachers.
                <br><br>
                Documents are used only for verifying that your teacher or faculty status is current and will be permanently
                deleted after review is finalized. They will not be shared with {companyName}`,
        },
        pending: {
            titleReview: 'Document in review',
            subtitle: 'We received your document! We\'re reviewing now to verify your instructor status.',
            subtitle2: 'This should take less than 20 minutes. This page will update with your verification status and we\'ll also send you an email with your status, so you don\'t have to stay here to get that information.',
            subtitle3: 'If you don\'t receive it, check your spam folder for mail from verify@sheerid.com.',
        },
        success: {
            title: 'Welcome to Intuit Education!',
            createAccount: '<a class="sid-link" href="{accessLink}">Create your account</a>',
            intuitTeamUS: 'Thanks!<br>The Intuit Team',
            bestWishesUS: "If you have any questions, please visit our <a href='https://www.intuit.com/partners/education-program/support/' class='sid-link'>support page</a>.",
            online: {
                title: 'Welcome to Intuit QuickBooks',
                subtitle: 'We have successfully verified your educator credentials and your account is ready!',
                subtitleCA: "We've verified your instructor status.",
                bestWishes: 'Best wishes on your teaching journey with QuickBooks!',
                bullet1: 'Setup classes',
                bullet2: 'Invite students to their own Quickbooks Online Plus companies.',
                nextSteps: 'Next steps to access the Intuit Educator Portal:',
                nextStepsCA: 'Next step:',
                getStarted: '<strong>Steps to getting started:</strong>',
                createAccount: '<a class="sid-link" href="https://signup.quickbooks.intuit.com/?locale=en-ca&offerType=trial&bc=OBI-LL1&offerId=20001253&product=qboa&productRegion=EN-CA&sc=PAP-GEN3-QBOA-FIRM">Create your Quickbooks Online Accountant account</a>. When prompted to enter details about your "firm", use details about your educational institution.',
                onlineLogin: "<a class='sid-link' href='{educatorPortalLink}'>Login to the Intuit Educator Portal</a> using your new login credentials.",
                checkOut: "Check out the “How the Educator Portal Works” video on the Portal to learn how to set up your class(es), invite your students, and access resources.",
            },
            desktop: {
                title: 'Welcome to Intuit QuickBooks',
                subtitle: 'We have successfully verified your educator credentials. You will receive an email within 3 business days containing your product license information and download link.',
                subtitleCA: 'We verified your instructor status. To access our complimentary QuickBooks Desktop software, please click the link below and then click "save as", choose a file location, and then run the installation. Please note that QuickBooks Desktop is not compatible with MAC computers.',
                accessLink: 'You will receive an email within 3 business days containing your product registration information.',
                bulletOne: 'Activate with your license code: {rewardCode}',
                bulletTwo: 'Activate with your product key: {productCode}',
                bulletThree: '<a href="{accessLink}" class="sid-link">Download product here</a>',
                bestWishes: "Best wishes on your teaching journey with Quickbooks! If you have any questions, <a class='sid-link' href='mailto:education@intuit.com'>Education Support</a> can help.",
                contactIntuit: "If you have any questions, <a class='sid-link' href='mailto:education@intuit.com'>Education Support</a> can help.",
            },
            tax: {
                title: 'Welcome to Intuit ProFile',
                titleUS: 'Welcome to Intuit ProConnect',
                subtitle: 'We are pleased to provide you with a complimentary trial of ProConnect Tax Online, the #1 cloud-based tax preparation software.',
                subtitleCA: 'We are pleased to provide you with complimentary access to ProFile tax software.',
                accessLink: '<a class="sid-link" href="{accessLink}">Create your account</a>',
                downloadLinkInstructionsCA: 'You will receive your download instructions and license key within 3 business days.',
                bestWishes: 'Best wishes on your teaching journey with ProFile! If you have any questions, <a href="mailto:iepcanada@intuit.com">Education Support</a> can help.',
                bestWishesUS: 'Best wishes on your teaching journey with ProConnect!',
            },
        },
    },
    infoShared: 'Information entered into this web form will be used for verification purposes. The information will be shared with Intuit. All use of Intuit products must comply with United States export control and economic sanction laws. <br /><br /> Intuit will use the personal information you provide to set up and manage your trial, and to communicate important information about your service. Click <a class="sid-link" href="https://security.intuit.com/index.php/privacy" target="_blank">here</a> to view our full Privacy Statement.',
};

